import React, { useState } from 'react';
import { 
  Box, Paper, Typography, Grid, TextField, Button, MenuItem, Tooltip, IconButton, Alert, 
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import api from '../../api';

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hourStr, minStr] = timeStr.split(':');
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${minStr} ${ampm}`;
};

const getShiftStyle = (shiftObj) => {
  if (!shiftObj) return { bg: '#f5f5f5', text: '#9e9e9e' };
  if (shiftObj.shift_name.toUpperCase().includes('WEEK OFF') || shiftObj.shift_name.toUpperCase().includes('WO')) {
    return { bg: '#607d8b', text: '#ffffff' };
  }
  const richPalettes = [
    { bg: '#e3f2fd', text: '#1565c0' }, { bg: '#e8f5e9', text: '#2e7d32' },
    { bg: '#fff3e0', text: '#e65100' }, { bg: '#f3e5f5', text: '#6a1b9a' },
    { bg: '#e0f7fa', text: '#006064' }, { bg: '#fce4ec', text: '#c2185b' },
  ];
  return richPalettes[shiftObj.id % richPalettes.length];
};

const RosterCalendarSection = ({ 
  activeMenu, selectedDate, changeMonth, calendarData, shifts, team, 
  selectedTeamMember, setSelectedTeamMember, assignForm, setAssignForm, 
  woAssignDate, setWoAssignDate, handleAssignShift, handleAssignWo, targetEmpWOs, refreshData 
}) => {
  
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedWoReq, setSelectedWoReq] = useState(null);
  const [newWoDate, setNewWoDate] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });

  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg, type });

  const handleDayClick = (dateStr, isApprovedWO) => {
    if (activeMenu === 'my_team' && isApprovedWO && isApprovedWO.id) {
      setSelectedWoReq(isApprovedWO);
      setNewWoDate(''); 
      setRescheduleReason('');
      setRescheduleDialogOpen(true);
    }
  };

  const submitReschedule = async () => {
    if (!newWoDate || !rescheduleReason) return showMsg("Both Date and Reason are required.", "error");
    try {
      const managerId = localStorage.getItem('emp_id');
      await api.put(`/weekoff/reschedule/${selectedWoReq.id}`, {
        new_date: newWoDate,
        manager_id: managerId,
        reason: rescheduleReason
      });
      showMsg("Week Off rescheduled successfully!");
      setRescheduleDialogOpen(false);
      
      // Auto-refresh the calendar data seamlessly, or fallback to a soft reload
      if (typeof refreshData === 'function') {
        refreshData(); 
      } else {
        setTimeout(() => { window.location.reload(); }, 1000);
      }

    } catch (err) {
      showMsg(err.response?.data?.detail || "Failed to reschedule WO.", "error");
    }
  };

  const renderCalendar = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const totalSlots = [...Array(firstDayOfMonth).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, bgcolor: '#f5f5f5', p: 1, borderRadius: 2 }}>
          <IconButton onClick={() => changeMonth(-1)}><ArrowBackIosIcon fontSize="small" /></IconButton>
          <Typography variant="h6" color="primary">{monthNames[month]} {year}</Typography>
          <IconButton onClick={() => changeMonth(1)}><ArrowForwardIosIcon fontSize="small" /></IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <Typography key={day} align="center" variant="subtitle2" color="textSecondary">{day}</Typography>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))', gap: 1, overflowX: 'auto' }}>
          {totalSlots.map((dayNum, idx) => {
            if (!dayNum) return <Box key={`blank-${idx}`} sx={{ height: 65 }} />;

            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
            const isApprovedWO = targetEmpWOs.find(wo => wo.date === dateStr);
            
            let shiftObj = null;
            if (isApprovedWO) {
               shiftObj = { id: 999, shift_name: 'Approved Week Off', start_time: '', end_time: '' };
            } else {
               const rosterEntry = calendarData.find(c => c.roster_date === dateStr);
               shiftObj = rosterEntry ? shifts.find(s => s.id === rosterEntry.shift_id) : null;
            }

            const shiftName = shiftObj ? shiftObj.shift_name : 'Unallocated';
            const shiftTimeStr = shiftObj && shiftObj.start_time ? `${formatTime(shiftObj.start_time)}` : (isApprovedWO ? 'Day Off' : 'None');
            const fullShiftTime = shiftObj && shiftObj.start_time ? `${formatTime(shiftObj.start_time)} - ${formatTime(shiftObj.end_time)}` : (isApprovedWO ? 'Full Day' : '--:-- to --:--');
            const style = getShiftStyle(shiftObj);
            const isClickableWO = activeMenu === 'my_team' && isApprovedWO;

            return (
              <Tooltip key={dayNum} title={
                <Box sx={{ p: 1, minWidth: 160 }}>
                  <Typography variant="subtitle2" sx={{ borderBottom: '1px solid rgba(255,255,255,0.3)', pb: 0.5, mb: 0.5 }}>Date: {dateStr}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Shift: {shiftName}</Typography>
                  <Typography variant="body2">Time: {fullShiftTime}</Typography>
                  {isClickableWO && <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#ffeb3b' }}>Click to Reschedule WO</Typography>}
                </Box>
              } arrow placement="top">
                <Paper 
                  onClick={() => handleDayClick(dateStr, isApprovedWO)}
                  sx={{
                    height: 65, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    bgcolor: style.bg, color: style.text, cursor: isClickableWO ? 'pointer' : 'default',
                    transition: 'transform 0.1s', '&:hover': { transform: isClickableWO ? 'scale(1.05)' : 'none' }, border: isClickableWO ? '2px dashed rgba(255,255,255,0.8)' : 'none'
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{dayNum}</Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{shiftTimeStr}</Typography>
                </Paper>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        {activeMenu === 'my_own' ? 'My Monthly Roster' : 'Team Shift Management'}
      </Typography>
      
      {activeMenu === 'my_own' && <Paper sx={{ p: 3 }}>{renderCalendar()}</Paper>}

      {activeMenu === 'my_team' && (
        <Grid container spacing={4}>
          <Grid item xs={12} xl={8}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <TextField select sx={{ minWidth: 250 }} label="Select Team Member" value={selectedTeamMember} onChange={e => setSelectedTeamMember(e.target.value)}>
                  {team.map(t => <MenuItem key={t.emp_id} value={t.emp_id}>{t.first_name} {t.last_name}</MenuItem>)}
                </TextField>
              </Box>
              {selectedTeamMember ? renderCalendar() : (
                <Alert sx={{ mt: 3 }} severity="info" variant="outlined">Please select a team member to view their calendar.</Alert>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} xl={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper sx={{ p: 3, opacity: selectedTeamMember ? 1 : 0.5, pointerEvents: selectedTeamMember ? 'auto' : 'none' }}>
                <Typography variant="subtitle1" gutterBottom>Directly Assign Shift</Typography>
                <Box component="form" onSubmit={handleAssignShift} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField type="date" label="Start Date" required InputLabelProps={{ shrink: true }} onChange={e => setAssignForm({ ...assignForm, start_date: e.target.value })} />
                  <TextField type="date" label="End Date" required InputLabelProps={{ shrink: true }} onChange={e => setAssignForm({ ...assignForm, end_date: e.target.value })} />
                  <TextField select label="Shift to Assign" required value={assignForm.requested_shift_id || ''} onChange={e => setAssignForm({ ...assignForm, requested_shift_id: e.target.value })}>
                    {shifts.map(s => <MenuItem key={s.id} value={s.id}>{s.shift_name}</MenuItem>)}
                  </TextField>
                  <Button type="submit" variant="contained" color="primary">Assign to Calendar</Button>
                </Box>
              </Paper>
              <Paper sx={{ p: 3, bgcolor: '#f8fafc', opacity: selectedTeamMember ? 1 : 0.5, pointerEvents: selectedTeamMember ? 'auto' : 'none' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main' }}>Assign Policy Week Off</Typography>
                <Box component="form" onSubmit={handleAssignWo} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField type="date" label="Date for WO" required InputLabelProps={{ shrink: true }} value={woAssignDate} onChange={e => setWoAssignDate(e.target.value)} />
                  <Button type="submit" variant="contained" color="primary">Validate & Assign WO</Button>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* COMPACT MANAGER RESCHEDULE WO DIALOG */}
      <Dialog open={rescheduleDialogOpen} onClose={() => setRescheduleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', pb: 1.5, fontSize: '1.1rem' }}>
          Reschedule Week Off
        </DialogTitle>
        <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}>
          <Alert severity="info" sx={{ py: 0, '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
            Moving WO from: <strong>{selectedWoReq?.date}</strong>
          </Alert>
          <TextField size="small" type="date" label="New Date" required InputLabelProps={{ shrink: true }} value={newWoDate} onChange={(e) => setNewWoDate(e.target.value)} fullWidth />
          <TextField size="small" label="Reason (Audit Trail)" required multiline rows={2} value={rescheduleReason} onChange={(e) => setRescheduleReason(e.target.value)} fullWidth placeholder="e.g., Business requirement" />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button size="small" onClick={() => setRescheduleDialogOpen(false)} color="inherit">Cancel</Button>
          <Button size="small" onClick={submitReschedule} variant="contained" color="primary" disableElevation>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.type} variant="filled" sx={{ width: '100%' }}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default RosterCalendarSection;